// One's own picture (21-user-settings.md, My account, rules 1, 1b and 3): Upload, Choose a picture and
// Remove picture, the same in My account and in the picture choice of the status menu's header card and
// the phone's You (34-status.md, v1.58.49). The spinner over the rail's and You's picture while one goes up.
// Each change is then set in every circle on this device (38-desyr-id-picture-key.md, Behaviour 1, v1.58.50).
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Confirm, Dialog } from '../blocks/Dialog';
import { Menu, MenuItem } from '../blocks/Menu';
import { toast } from '../blocks/Toast';
import { useAction } from '../blocks/Wait';
import { useBackCloses } from '../accounts/YourCircles';
import { t } from '../i18n';
import { meChanged } from './Account';
import { ChoosePicture } from './Presets';
import { pictureChanged } from '../id/onePicture';
import { forUpload, PICTURE_TYPES } from '../id/pictureFile';

let going = 0;
const watchers = new Set<() => void>();
const tell = (n: number) => ((going += n), watchers.forEach((w) => w()));
const watch = (w: () => void) => (watchers.add(w), () => void watchers.delete(w));
/** A new picture is going up (34, v1.58.49: the rail's and You's picture show the spinner and open nothing). */
export const usePictureGoing = () => useSyncExternalStore(watch, () => going > 0);

/** Uploads `file` and sets it as the person's picture (rules 1 and 1b: an ordinary upload). */
export async function putPicture(client: MatrixClient, file: Blob, opts: { type: string; name?: string } = { type: file.type }) {
  tell(1);
  try {
    const { content_uri } = await client.uploadContent(file, opts);
    await client.setAvatarUrl(content_uri);
    meChanged();
    void pictureChanged(client, file.type ? file : new Blob([file], { type: opts.type }), content_uri); // then in every circle (38, v1.58.50)
  } finally {
    tell(-1);
  }
}

export type PictureChange = ReturnType<typeof usePicture>;

/**
 * The picture's three actions, with the file picker and the dialogs they open (`parts`, drawn by the
 * owner). Upload's failures are My account's card line (`inCard`), elsewhere a toast (34, v1.58.49).
 */
export function usePicture(client: MatrixClient, inCard = false) {
  // Rule 3: the server's capabilities may forbid it.
  const [may, setMay] = useState(() => client.getCachedCapabilities?.()?.['m.set_avatar_url']?.enabled !== false);
  const [error, setError] = useState<string>();
  const [choosing, setChoosing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  useEffect(() => void client.getCapabilities().then((c) => setMay(c['m.set_avatar_url']?.enabled !== false), () => {}), [client]);
  const upload = useAction((f: Blob) => putPicture(client, f), 'save');
  const failed = upload.message && `${t('settings.pictureFailed')} ${upload.message}`;
  useEffect(() => void (!inCard && failed && toast(failed)), [failed]); // eslint-disable-line react-hooks/exhaustive-deps
  const say = (text: string) => (inCard ? setError(text) : toast(text));
  const parts = (
    <>
      <input
        ref={file}
        type="file"
        accept={PICTURE_TYPES}
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          // A JPEG, PNG or WebP told by its bytes, scaled to 1024 px when larger (rule 1, v1.58.63).
          const picture = await forUpload(f).catch(() => undefined);
          if (!picture) return say(t('settings.notPicture'));
          setError(undefined);
          void upload.run(picture);
        }}
      />
      {choosing && <ChoosePicture client={client} onClose={() => setChoosing(false)} />}
      {removing && (
        <Confirm
          title={t('settings.removePicture')}
          text={t('settings.removePictureText')}
          action={t('settings.remove')}
          destructive
          errorPrefix={`${t('settings.pictureNotRemoved')} `}
          onConfirm={() => client.setAvatarUrl('').then(() => (meChanged(), void pictureChanged(client, null)))}
          onClose={() => setRemoving(false)}
        />
      )}
    </>
  );
  return {
    may,
    busy: upload.busy,
    error: error ?? (inCard ? failed : undefined),
    upload: () => file.current?.click(),
    choose: () => setChoosing(true),
    remove: () => setRemoving(true),
    parts,
  };
}

/**
 * The picture choice (34, v1.58.49): My account's picture buttons as a menu at `place`, or on a phone
 * as a sheet like the status menu's; Remove picture only while there is a picture (`has`).
 */
export function PictureChoice({ picture, has, place, onClose }: { picture: PictureChange; has: boolean; place?: { x: number; y: number }; onClose: () => void }) {
  useBackCloses(onClose, !place);
  const menu = (
    <Menu label={t('common.changeAvatar')} point={place} inline={!place} className={place ? 'status-menu' : 'status-menu sheet'} onClose={onClose} data-desyr-avatar-choice="">
      <MenuItem onSelect={picture.upload}>{t('settings.upload')}</MenuItem>
      <MenuItem onSelect={picture.choose}>{t('presets.title')}</MenuItem>
      {has && (
        <MenuItem destructive onSelect={picture.remove}>
          {t('settings.removePicture')}
        </MenuItem>
      )}
    </Menu>
  );
  return place ? menu : <Dialog label={t('common.changeAvatar')} className="status-sheet" onClose={onClose}>{menu}</Dialog>;
}
